import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ProgramSlideBgmModal from '../program/ProgramSlideBgmModal';
import { useMainPresentationBgm, type MainPresentationBgmController } from '../../hooks/useMainPresentationBgm';
import ProgramSlideDiceModal from '../program/ProgramSlideDiceModal';
import EduCoordiTimerModal from './EduCoordiTimerModal';
import {
  ProgramSlideWebcamDrawRoot,
  ProgramSlideWebcamDrawCanvas,
  ProgramSlideWebcamDrawToolbar,
  type WebcamDrawStroke,
} from '../program/ProgramSlideWebcamDrawOverlay';
import { useWebcamViewTransform } from '../../hooks/useWebcamViewTransform';
import { useSlideLiveCaptionRecognition } from '../../hooks/useSlideLiveCaptionRecognition';
import { useAppTheme } from '../../hooks/useAppTheme';
import { primeTimerOneMinSoundCache } from '../../utils/timerOneMinuteSoundCache';
import {
  filterVideoInputDevices,
  getSlideScreenShareMedia,
  getSlideWebcamUserMedia,
  getSlideWebcamUserMediaSmartDefault,
  simplifyVideoInputDeviceLabel,
} from '../../utils/programSlideWebcamQuality';
import {
  formatPageZoomPercent,
  PAGE_ZOOM_CHANGED_EVENT,
  PAGE_ZOOM_MAX,
  PAGE_ZOOM_MIN,
  readPageZoom,
  stepPageZoom,
} from '../../utils/pageZoom';

/* —— 전체화면 유틸(수업 프레젠테이션과 동일 동작) —— */
type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FullscreenEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function requestPresentationFullscreen() {
  const el = document.documentElement as FullscreenEl;
  if (el.requestFullscreen) void el.requestFullscreen();
  else if (el.webkitRequestFullscreen) void el.webkitRequestFullscreen();
}
function exitPresentationFullscreen() {
  const doc = document as FullscreenDoc;
  if (doc.exitFullscreen) void doc.exitFullscreen();
  else if (doc.webkitExitFullscreen) void doc.webkitExitFullscreen();
}
function isPresentationFullscreenActive(): boolean {
  const doc = document as FullscreenDoc;
  return !!(doc.fullscreenElement ?? doc.webkitFullscreenElement);
}

type WebcamCaptureMode = 'camera' | 'screen' | 'screen_camera';
type CaptionWinMode = 'single-center' | 'two-line' | 'tail';

const FullscreenExpandIcon: React.FC = () => (
  <svg
    className="cp-program-slide-fullscreen-btn-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M8 3H3v5" />
    <path d="M16 3h5v5" />
    <path d="M8 21H3v-5" />
    <path d="M16 21h5v-5" />
  </svg>
);
const FullscreenShrinkIcon: React.FC = () => (
  <svg
    className="cp-program-slide-fullscreen-btn-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 14h6v6" />
    <path d="M20 10h-6V4" />
    <path d="M14 10l7-7" />
    <path d="M10 14l-7 7" />
  </svg>
);

export type MainPresentationViewMode = 'guide' | 'board' | 'assignmentCard' | 'pointItem';
/** 칠판 캔버스: 편집 / 보기(슬라이드 viewMode와 동일) */
export type MainPresentationBoardCanvasMode = 'edit' | 'view';

export interface MainPresentationToolbarProps {
  workspaceId: string;
  gradeId: string;
  viewMode: MainPresentationViewMode;
  onViewModeChange: (mode: MainPresentationViewMode) => void;
  boardCanvasMode: MainPresentationBoardCanvasMode;
  onBoardCanvasModeChange: (mode: MainPresentationBoardCanvasMode) => void;
  /** 칠판 현재 페이지의 판서(페이지별 임시 보관은 호스트가 담당) */
  boardDrawStrokes: WebcamDrawStroke[];
  onBoardDrawStrokesChange: (next: WebcamDrawStroke[]) => void;
  /** 페이지 소유 BGM 컨트롤러 (없으면 툴바 내부 상태 사용 — 기존 동작) */
  bgm?: MainPresentationBgmController;
  /** 프리 이용권: 과제 카드·포인트 탭 잠금 표시 (클릭 시 호스트가 유료 안내 모달) */
  paidTabsLocked?: boolean;
  /** 이용권별 기본 기능 권한: 과제 카드 탭만 잠금 (없으면 paidTabsLocked) */
  assignmentTabLocked?: boolean;
  /** 이용권별 기본 기능 권한: 포인트 탭만 잠금 (없으면 paidTabsLocked) */
  pointTabLocked?: boolean;
}

const MainPresentationToolbar: React.FC<MainPresentationToolbarProps> = ({
  workspaceId,
  gradeId,
  viewMode,
  onViewModeChange,
  boardCanvasMode,
  onBoardCanvasModeChange,
  boardDrawStrokes,
  onBoardDrawStrokesChange,
  bgm,
  paidTabsLocked = false,
  assignmentTabLocked,
  pointTabLocked,
}) => {
  const assignmentLocked = assignmentTabLocked ?? paidTabsLocked;
  const pointLocked = pointTabLocked ?? paidTabsLocked;
  const theme = useAppTheme();
  const slideSurface = theme === 'light' ? 'light' : 'dark';

  /* —— 타이머 (ClassLab EduCoordi 플로팅 모달) —— */
  const [timerModalOpen, setTimerModalOpen] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);

  /* —— BGM (페이지가 소유하면 props로 받음 — 리모컨과 같은 핸들러 공유) —— */
  const internalBgm = useMainPresentationBgm();
  const { bgmModalOpen, setBgmModalOpen, bgmTransport, handleBgmPick, handleBgmPauseToggle, handleBgmStop } =
    bgm ?? internalBgm;

  /* —— 주사위 —— */
  const [diceModalOpen, setDiceModalOpen] = useState(false);

  /* —— 실시간 자막 —— */
  const {
    active: captionOn,
    text: captionText,
    error: captionError,
    start: startCaption,
    stop: stopCaption,
    isSupported: captionSupported,
  } = useSlideLiveCaptionRecognition();

  /** 칠판 진입 직전 화면 — 아이콘으로 닫을 때 복귀 */
  const boardReturnModeRef = useRef<Exclude<MainPresentationViewMode, 'board'>>('guide');

  const openBoard = useCallback(
    (canvasMode: MainPresentationBoardCanvasMode) => {
      if (viewMode !== 'board') {
        boardReturnModeRef.current = viewMode;
      }
      onViewModeChange('board');
      onBoardCanvasModeChange(canvasMode);
    },
    [viewMode, onViewModeChange, onBoardCanvasModeChange]
  );

  const closeBoard = useCallback(() => {
    onViewModeChange(boardReturnModeRef.current);
  }, [onViewModeChange]);

  /* 슬라이드와 동일: 창 높이는 딱 2줄, 넘치면 is-caption-tail로 아래 2줄만 보이게 밀림 */
  const captionInnerRef = useRef<HTMLDivElement | null>(null);
  const [captionWinMode, setCaptionWinMode] = useState<CaptionWinMode>('two-line');

  useLayoutEffect(() => {
    if (!captionOn) {
      setCaptionWinMode('two-line');
      return;
    }
    const inner = captionInnerRef.current;
    const winEl = inner?.parentElement;
    if (!inner || !winEl) return;
    const measure = () => {
      const ch = winEl.clientHeight;
      const sh = inner.scrollHeight;
      const lhRaw = getComputedStyle(inner).lineHeight;
      let linePx = parseFloat(lhRaw);
      if (!Number.isFinite(linePx) || linePx <= 0) {
        linePx = ch / 2;
      }
      const singleLineTol = 6;
      if (sh <= linePx + singleLineTol) {
        setCaptionWinMode('single-center');
      } else if (sh <= ch + 1) {
        setCaptionWinMode('two-line');
      } else {
        setCaptionWinMode('tail');
      }
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(winEl);
      ro.observe(inner);
    }
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [captionOn, captionText]);

  /* —— 카메라(웹캠 판서) —— */
  const [webcamOn, setWebcamOn] = useState(false);
  const [webcamLoading, setWebcamLoading] = useState(false);
  const [webcamError, setWebcamError] = useState('');
  const [webcamStrokes, setWebcamStrokes] = useState<WebcamDrawStroke[]>([]);
  const {
    controls: webcamViewControls,
    transformed: webcamViewTransformed,
    style: webcamViewStyle,
  } = useWebcamViewTransform(() => webcamStreamRef.current?.getVideoTracks()[0] ?? null);
  const [webcamCaptureMode, setWebcamCaptureMode] = useState<WebcamCaptureMode>('camera');
  const [webcamCameraDeviceId, setWebcamCameraDeviceId] = useState<string | null>(null);
  const [webcamDeviceList, setWebcamDeviceList] = useState<MediaDeviceInfo[]>([]);
  const [webcamSourceMenuOpen, setWebcamSourceMenuOpen] = useState(false);
  const [frozenFrameUrl, setFrozenFrameUrl] = useState<string | null>(null);
  const webcamVideoRef = useRef<HTMLVideoElement | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const webcamSourceMenuRef = useRef<HTMLDivElement | null>(null);
  const webcamEffectGenRef = useRef(0);
  const webcamFrozenRef = useRef(false);
  const frozenBlobUrlRef = useRef<string | null>(null);
  const freezePendingRef = useRef(false);
  const freezeGenRef = useRef(0);

  /* —— 전체화면 —— */
  const [fullscreen, setFullscreen] = useState(false);
  const [pageZoom, setPageZoomLevel] = useState(() => readPageZoom());

  useEffect(() => {
    primeTimerOneMinSoundCache();
  }, []);

  const handleTimerNavButtonClick = useCallback(() => {
    setTimerModalOpen(true);
  }, []);


  // 모달 열림 시 배경 스크롤 잠금 (플로팅 타이머는 제외)
  useEffect(() => {
    if (!bgmModalOpen && !diceModalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [bgmModalOpen, diceModalOpen]);


  const releaseFrozenFrame = useCallback(() => {
    freezeGenRef.current += 1;
    freezePendingRef.current = false;
    if (frozenBlobUrlRef.current) {
      URL.revokeObjectURL(frozenBlobUrlRef.current);
      frozenBlobUrlRef.current = null;
    }
    webcamFrozenRef.current = false;
    setFrozenFrameUrl(null);
  }, []);

  const stopAllWebcamPreview = useCallback(() => {
    const cam = webcamStreamRef.current;
    if (cam) {
      cam.getTracks().forEach((t) => t.stop());
      webcamStreamRef.current = null;
    }
    const scr = screenStreamRef.current;
    if (scr) {
      scr.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    if (webcamVideoRef.current) webcamVideoRef.current.srcObject = null;
    if (screenVideoRef.current) screenVideoRef.current.srcObject = null;
  }, []);

  const stopWebcam = useCallback(() => {
    releaseFrozenFrame();
    stopAllWebcamPreview();
    setWebcamLoading(false);
    setWebcamOn(false);
    setWebcamSourceMenuOpen(false);
    setWebcamStrokes([]);
  }, [releaseFrozenFrame, stopAllWebcamPreview]);

  /** 카메라 버튼: 켜져 있으면 네비의 정지 버튼으로만 끔(수업 프레젠테이션과 동일) */
  const handleWebcamCameraClick = useCallback(() => {
    if (webcamLoading) return;
    setWebcamSourceMenuOpen(false);
    if (webcamOn) return;
    setWebcamError('');
    setWebcamOn(true);
  }, [webcamLoading, webcamOn]);

  const runWebcamFreezeCapture = useCallback((): boolean => {
    const v = webcamVideoRef.current;
    if (!v || !webcamOn) return false;
    if (webcamFrozenRef.current || frozenBlobUrlRef.current) return true;
    if (freezePendingRef.current) return false;

    const w = v.videoWidth;
    const h = v.videoHeight;
    if (w <= 0 || h <= 0) return false;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    try {
      ctx.drawImage(v, 0, 0, w, h);
    } catch {
      return false;
    }

    freezePendingRef.current = true;
    freezeGenRef.current += 1;
    const gen = freezeGenRef.current;

    canvas.toBlob(
      (blob) => {
        freezePendingRef.current = false;
        if (!blob || gen !== freezeGenRef.current || !webcamStreamRef.current) return;
        const url = URL.createObjectURL(blob);
        if (frozenBlobUrlRef.current) URL.revokeObjectURL(frozenBlobUrlRef.current);
        frozenBlobUrlRef.current = url;
        webcamFrozenRef.current = true;
        setFrozenFrameUrl(url);
        void webcamVideoRef.current?.pause();
      },
      'image/jpeg',
      0.94
    );
    return true;
  }, [webcamOn]);

  const toggleWebcamFreeze = useCallback(() => {
    if (webcamLoading) return;
    const v = webcamVideoRef.current;
    if (!v || !webcamOn) return;

    if (webcamFrozenRef.current || frozenBlobUrlRef.current) {
      releaseFrozenFrame();
      void v.play().catch(() => {});
      return;
    }
    runWebcamFreezeCapture();
  }, [webcamOn, webcamLoading, releaseFrozenFrame, runWebcamFreezeCapture]);

  // 고화질 getUserMedia / 화면공유 — 수업 프레젠테이션과 동일 유틸
  useEffect(() => {
    if (!webcamOn) {
      releaseFrozenFrame();
      stopAllWebcamPreview();
      setWebcamLoading(false);
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setWebcamError('이 환경에서는 카메라를 사용할 수 없습니다. HTTPS 접속인지 확인해 주세요.');
      setWebcamOn(false);
      return;
    }

    let cancelled = false;
    let removeScreenEndedListener: (() => void) | undefined;
    const runGen = (webcamEffectGenRef.current += 1);
    const alive = () => !cancelled && runGen === webcamEffectGenRef.current;

    const attachScreenEnded = (stream: MediaStream, onEnd: () => void) => {
      const t = stream.getVideoTracks()[0];
      if (!t) return () => {};
      t.addEventListener('ended', onEnd);
      return () => t.removeEventListener('ended', onEnd);
    };

    setWebcamLoading(true);
    setWebcamError('');

    void (async () => {
      try {
        stopAllWebcamPreview();
        if (!alive()) return;

        const mode = webcamCaptureMode;
        const camId = webcamCameraDeviceId?.trim() || null;

        if (mode === 'camera') {
          const stream =
            camId != null && camId !== ''
              ? await getSlideWebcamUserMedia({ deviceId: camId })
              : await getSlideWebcamUserMediaSmartDefault();
          if (!alive()) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          webcamStreamRef.current = stream;
          const v = webcamVideoRef.current;
          if (v) {
            v.srcObject = stream;
            void v.play().catch(() => {});
          }
        } else if (mode === 'screen') {
          const stream = await getSlideScreenShareMedia();
          if (!alive()) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          screenStreamRef.current = stream;
          removeScreenEndedListener = attachScreenEnded(stream, () => {
            if (cancelled) return;
            setWebcamOn(false);
          });
          const v = webcamVideoRef.current;
          if (v) {
            v.srcObject = stream;
            void v.play().catch(() => {});
          }
        } else {
          const scr = await getSlideScreenShareMedia();
          if (!alive()) {
            scr.getTracks().forEach((t) => t.stop());
            return;
          }
          screenStreamRef.current = scr;
          removeScreenEndedListener = attachScreenEnded(scr, () => {
            if (cancelled) return;
            setWebcamOn(false);
          });
          const sv = screenVideoRef.current;
          if (sv) {
            sv.srcObject = scr;
            void sv.play().catch(() => {});
          }

          const camStream =
            camId != null && camId !== ''
              ? await getSlideWebcamUserMedia({ deviceId: camId })
              : await getSlideWebcamUserMediaSmartDefault();
          if (!alive()) {
            scr.getTracks().forEach((t) => t.stop());
            camStream.getTracks().forEach((t) => t.stop());
            return;
          }
          webcamStreamRef.current = camStream;
          const pv = webcamVideoRef.current;
          if (pv) {
            pv.srcObject = camStream;
            void pv.play().catch(() => {});
          }
        }
      } catch (err: unknown) {
        if (!alive()) return;
        stopAllWebcamPreview();
        const name = err && typeof err === 'object' && 'name' in err ? String((err as { name?: string }).name) : '';
        if (
          name === 'NotAllowedError' &&
          (webcamCaptureMode === 'screen' || webcamCaptureMode === 'screen_camera')
        ) {
          setWebcamCaptureMode('camera');
          setWebcamError('화면 공유가 취소되었습니다.');
          window.setTimeout(() => setWebcamError(''), 4200);
          return;
        }
        const msg =
          name === 'NotAllowedError'
            ? '카메라 사용이 거부되었습니다.'
            : err instanceof Error
              ? err.message
              : '미디어를 켤 수 없습니다.';
        setWebcamError(msg);
        setWebcamOn(false);
        window.setTimeout(() => setWebcamError(''), 4200);
      } finally {
        if (!cancelled && runGen === webcamEffectGenRef.current) setWebcamLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      removeScreenEndedListener?.();
      releaseFrozenFrame();
      stopAllWebcamPreview();
    };
  }, [
    webcamOn,
    webcamCaptureMode,
    webcamCameraDeviceId,
    releaseFrozenFrame,
    stopAllWebcamPreview,
  ]);

  useEffect(() => {
    if (!webcamSourceMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const el = webcamSourceMenuRef.current;
      if (el && !el.contains(e.target as Node)) setWebcamSourceMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [webcamSourceMenuOpen]);

  // 전체화면 상태 동기화
  useEffect(() => {
    const onChange = () => setFullscreen(isPresentationFullscreenActive());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange as EventListener);
    onChange();
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange as EventListener);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isPresentationFullscreenActive()) exitPresentationFullscreen();
    else requestPresentationFullscreen();
  }, []);

  useEffect(() => {
    const onPageZoomChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ zoom?: number }>).detail;
      if (typeof detail?.zoom === 'number') {
        setPageZoomLevel(detail.zoom);
        return;
      }
      setPageZoomLevel(readPageZoom());
    };
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChanged as EventListener);
    return () => {
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onPageZoomChanged as EventListener);
    };
  }, []);

  const handlePageZoomOut = useCallback(() => {
    setPageZoomLevel(stepPageZoom(-1));
  }, []);

  const handlePageZoomIn = useCallback(() => {
    setPageZoomLevel(stepPageZoom(1));
  }, []);

  const webcamNavTools = webcamOn ? (
    <>
      <div
        className="cp-program-slide-webcam-source-wrap cp-program-slide-webcam-source-wrap--topnav"
        ref={webcamSourceMenuRef}
      >
        <button
          type="button"
          className="cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--transport"
          disabled={webcamLoading}
          title={webcamLoading ? '미디어 연결 중…' : '카메라·화면 입력 바꾸기'}
          aria-label="카메라 또는 화면 입력 바꾸기"
          aria-expanded={webcamSourceMenuOpen}
          aria-haspopup="true"
          onClick={(e) => {
            e.stopPropagation();
            setWebcamSourceMenuOpen((open) => {
              const next = !open;
              if (next && navigator.mediaDevices?.enumerateDevices) {
                void navigator.mediaDevices.enumerateDevices().then((ds) => {
                  setWebcamDeviceList(filterVideoInputDevices(ds));
                });
              }
              return next;
            });
          }}
        >
          <img
            src="/change.svg"
            alt=""
            className="cp-program-slide-webcam-swap-icon-topnav"
            width={15}
            height={15}
            aria-hidden
          />
        </button>
        {webcamSourceMenuOpen ? (
          <div
            className="cp-program-slide-webcam-source-menu cp-program-slide-webcam-source-menu--topnav"
            role="menu"
            aria-label="비디오 입력"
          >
            <div className="cp-program-slide-webcam-source-menu-section" role="presentation">
              {webcamDeviceList.length === 0 ? (
                <div className="cp-program-slide-webcam-source-menu-empty">장치 목록을 불러오는 중…</div>
              ) : (
                webcamDeviceList.map((d) => {
                  const label = simplifyVideoInputDeviceLabel(d.label.trim() || '카메라');
                  const selected =
                    webcamCaptureMode === 'camera' && webcamCameraDeviceId === d.deviceId;
                  return (
                    <button
                      key={d.deviceId}
                      type="button"
                      role="menuitem"
                      className={'cp-program-slide-webcam-source-item' + (selected ? ' is-selected' : '')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setWebcamCaptureMode('camera');
                        setWebcamCameraDeviceId(d.deviceId);
                        setWebcamSourceMenuOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  );
                })
              )}
            </div>
            <div className="cp-program-slide-webcam-source-menu-divider" role="separator" />
            <button
              type="button"
              role="menuitem"
              className={
                'cp-program-slide-webcam-source-item' + (webcamCaptureMode === 'screen' ? ' is-selected' : '')
              }
              onClick={(e) => {
                e.stopPropagation();
                setWebcamCaptureMode('screen');
                setWebcamSourceMenuOpen(false);
              }}
            >
              화면 공유
            </button>
            <button
              type="button"
              role="menuitem"
              className={
                'cp-program-slide-webcam-source-item' +
                (webcamCaptureMode === 'screen_camera' ? ' is-selected' : '')
              }
              onClick={(e) => {
                e.stopPropagation();
                setWebcamCaptureMode('screen_camera');
                setWebcamSourceMenuOpen(false);
              }}
            >
              화면 공유 + 웹캠
            </button>
          </div>
        ) : null}
      </div>
      <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
      <button
        type="button"
        className="cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--transport"
        data-tutorial-pres="webcam-pause"
        disabled={webcamLoading}
        title={
          webcamLoading ? '미디어 연결 중…' : frozenFrameUrl ? '미리보기 재생' : '미리보기 일시정지'
        }
        aria-label={
          webcamLoading ? '미디어 연결 중' : frozenFrameUrl ? '미리보기 재생' : '미리보기 일시정지'
        }
        aria-pressed={!!frozenFrameUrl}
        onClick={(e) => {
          e.stopPropagation();
          toggleWebcamFreeze();
        }}
      >
        <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
          {frozenFrameUrl ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M8 5.14v13.72c0 .89 1.02 1.39 1.71.83l9.06-6.86a1 1 0 0 0 0-1.6L9.71 4.31A1 1 0 0 0 8 5.14Z" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <rect x="7" y="6" width="3.5" height="12" rx="1" />
              <rect x="13.5" y="6" width="3.5" height="12" rx="1" />
            </svg>
          )}
        </span>
      </button>
      <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
      <button
        type="button"
        className="cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--transport"
        data-tutorial-pres="webcam-stop"
        title="미리보기 정지(끄기)"
        aria-label="미리보기 정지"
        onClick={(e) => {
          e.stopPropagation();
          stopWebcam();
        }}
      >
        <span className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect x="7" y="7" width="10" height="10" rx="1.5" />
          </svg>
        </span>
      </button>
    </>
  ) : null;

  return (
    <div className="cp-program-slide-root cp-dash-presentation-chrome" data-slide-surface={slideSurface}>
      <header className="cp-topnav cp-program-slide-topnav cp-program-slide-app-chrome">
        <div className="cp-topnav-left">
          <span className="cp-program-slide-topnav-title">메인 프레젠테이션</span>
          <div
            className="cp-dash-presentation-view-tabs"
            role="tablist"
            aria-label="메인 프레젠테이션 화면"
          >
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'guide'}
              className={
                'cp-theme-toggle cp-dash-presentation-view-tab' +
                (viewMode === 'guide' ? ' is-on' : '')
              }
              data-tutorial-pres="tab-guide"
              onClick={() => onViewModeChange('guide')}
              title="교시 블럭·교시 설명·시각"
            >
              <span className="cp-dash-presentation-view-tab-text cp-dash-presentation-view-tab-text--full">
                수업 및 시간안내
              </span>
              <span className="cp-dash-presentation-view-tab-text cp-dash-presentation-view-tab-text--short">
                시간안내
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'assignmentCard'}
              className={
                'cp-theme-toggle cp-dash-presentation-view-tab' +
                (viewMode === 'assignmentCard' ? ' is-on' : '') +
                (assignmentLocked ? ' is-pass-locked' : '')
              }
              data-tutorial-pres="tab-assignment"
              aria-disabled={assignmentLocked || undefined}
              onClick={() => onViewModeChange('assignmentCard')}
              title={assignmentLocked ? '유료 이용권 사용자 전용 기능입니다' : '과제 관리 카드'}
            >
              과제 카드
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'pointItem'}
              className={
                'cp-theme-toggle cp-dash-presentation-view-tab' +
                (viewMode === 'pointItem' ? ' is-on' : '') +
                (pointLocked ? ' is-pass-locked' : '')
              }
              data-tutorial-pres="tab-point"
              aria-disabled={pointLocked || undefined}
              onClick={() => onViewModeChange('pointItem')}
              title={pointLocked ? '유료 이용권 사용자 전용 기능입니다' : '포인트 · 코인 · 아이템'}
            >
              포인트
            </button>
          </div>
        </div>
        <div className="cp-program-slide-topnav-center">
          {viewMode === 'board' ? (
            <div
              className="cp-dash-presentation-board-seg"
              role="group"
              aria-label="칠판 편집·보기"
              data-tutorial-pres="board-modes"
            >
              <button
                type="button"
                className="cp-dash-presentation-board-seg-icon-btn"
                aria-label="칠판 모드 끄기"
                data-tutorial-pres="board-close"
                title="칠판 모드 끄기"
                onClick={closeBoard}
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/blackboard.svg`}
                  alt=""
                  className="cp-dash-presentation-board-icon"
                  aria-hidden
                />
              </button>
              <span className="cp-dash-presentation-board-seg-colon" aria-hidden>
                :
              </span>
              <button
                type="button"
                className={
                  'cp-dash-presentation-board-seg-btn' +
                  (boardCanvasMode === 'edit' ? ' is-on' : '')
                }
                aria-pressed={boardCanvasMode === 'edit'}
                onClick={() => openBoard('edit')}
                title="칠판 편집모드 — 블럭 삽입·편집"
              >
                편집
              </button>
              <span className="cp-dash-presentation-board-seg-divider" aria-hidden>
                |
              </span>
              <button
                type="button"
                className={
                  'cp-dash-presentation-board-seg-btn' +
                  (boardCanvasMode === 'view' ? ' is-on' : '')
                }
                aria-pressed={boardCanvasMode === 'view'}
                onClick={() => openBoard('view')}
                title="칠판 보기모드 — 퀴즈·버튼 등 보기모드 동작"
              >
                보기
              </button>
            </div>
          ) : (
            <button
              type="button"
              role="tab"
              aria-selected={false}
              aria-label="칠판"
              className="cp-theme-toggle cp-dash-presentation-view-tab cp-dash-presentation-view-tab--board-icon"
              data-tutorial-pres="tab-board"
              onClick={() => openBoard('edit')}
              title="임시 칠판 (저장 없음) — 클릭 시 편집/보기 선택"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/blackboard.svg`}
                alt=""
                className="cp-dash-presentation-board-icon"
                aria-hidden
              />
            </button>
          )}
          {/* 카메라 */}
          <div
            className={'cp-program-slide-webcam-nav-block' + (webcamOn ? ' is-webcam-on' : '')}
            role="group"
            aria-label="웹캠 미리보기"
          >
            <button
              type="button"
              aria-busy={webcamLoading || undefined}
              className={
                'cp-program-slide-webcam-nav-block-btn cp-program-slide-webcam-nav-block-btn--camera' +
                (webcamLoading ? ' is-webcam-connecting' : '')
              }
              data-tutorial-pres="webcam"
              onClick={handleWebcamCameraClick}
              title={
                webcamLoading
                  ? '미디어 연결 중…'
                  : webcamOn
                    ? '웹캠 미리보기 사용 중, 끄기는 정지 버튼'
                    : '웹캠 미리보기 켜기'
              }
              aria-pressed={webcamOn}
              aria-label={
                webcamLoading
                  ? '미디어 연결 중'
                  : webcamOn
                    ? '웹캠 미리보기 사용 중, 끄기는 정지 버튼'
                    : '웹캠 미리보기 켜기'
              }
            >
              <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                  <circle cx="12" cy="13" r="3" />
                </svg>
              </span>
            </button>
            {webcamNavTools ? (
              <>
                <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                {webcamNavTools}
              </>
            ) : null}
          </div>

          {/* 타이머 */}
          <button
            type="button"
            className={
              'cp-theme-toggle cp-program-slide-webcam-btn cp-program-slide-timer-btn' +
              (timerRunning ? ' is-on' : '')
            }
            data-tutorial-pres="timer"
            onClick={handleTimerNavButtonClick}
            title={timerRunning ? '타이머 실행 중' : '타이머 설정'}
            aria-pressed={timerRunning}
            aria-label={timerRunning ? '타이머 실행 중' : '타이머 설정'}
          >
            <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </span>
          </button>

          {/* BGM */}
          <div
            className={'cp-program-slide-bgm-nav-block' + (bgmTransport !== 'idle' ? ' is-bgm-on' : '')}
            role="group"
            aria-label="BGM"
          >
            <button
              type="button"
              className="cp-program-slide-bgm-nav-block-btn cp-program-slide-bgm-nav-block-btn--note"
              data-tutorial-pres="bgm"
              onClick={() => setBgmModalOpen(true)}
              title="BGM 선택"
              aria-label="BGM 선택"
            >
              <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M9 18V5l10-2v10" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="16" cy="16" r="3" />
                </svg>
              </span>
            </button>
            {bgmTransport !== 'idle' ? (
              <>
                <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                <button
                  type="button"
                  className="cp-program-slide-bgm-nav-block-btn cp-program-slide-bgm-nav-block-btn--transport"
                  onClick={handleBgmPauseToggle}
                  title={bgmTransport === 'paused' ? 'BGM 재생' : 'BGM 일시정지'}
                  aria-label={bgmTransport === 'paused' ? 'BGM 재생' : 'BGM 일시정지'}
                  aria-pressed={bgmTransport === 'playing'}
                >
                  <span
                    className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact"
                    aria-hidden
                  >
                    {bgmTransport === 'paused' ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                      >
                        <path d="M8 5v14l11-7L8 5z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                      >
                        <rect x="7" y="6" width="4" height="12" rx="1" />
                        <rect x="13" y="6" width="4" height="12" rx="1" />
                      </svg>
                    )}
                  </span>
                </button>
                <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                <button
                  type="button"
                  className="cp-program-slide-bgm-nav-block-btn cp-program-slide-bgm-nav-block-btn--transport"
                  data-tutorial-pres="bgm-stop"
                  data-tutorial-slide="bgm-stop"
                  onClick={handleBgmStop}
                  title="BGM 정지"
                  aria-label="BGM 정지"
                >
                  <span
                    className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact"
                    aria-hidden
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden
                    >
                      <rect x="7" y="7" width="10" height="10" rx="1.5" />
                    </svg>
                  </span>
                </button>
              </>
            ) : null}
          </div>

          {/* 주사위(뽑기) */}
          <button
            type="button"
            className="cp-theme-toggle cp-program-slide-webcam-btn cp-program-slide-dice-btn"
            data-tutorial-pres="dice"
            onClick={() => setDiceModalOpen(true)}
            title="뽑기"
            aria-label="뽑기"
          >
            <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
              <img src="/Dice.svg" alt="" width={18} height={18} />
            </span>
          </button>

          {/* 마이크(실시간 자막) */}
          {captionSupported ? (
            <div
              className={'cp-program-slide-caption-nav-block' + (captionOn ? ' is-caption-on' : '')}
              role="group"
              aria-label="실시간 자막"
            >
              <button
                type="button"
                className="cp-program-slide-caption-nav-block-btn cp-program-slide-caption-nav-block-btn--mic"
                data-tutorial-pres="caption"
                onClick={() => startCaption()}
                disabled={captionOn}
                title={captionOn ? '자막 인식 중 (끄기: 정지)' : '실시간 자막 켜기'}
                aria-pressed={captionOn}
                aria-label={captionOn ? '자막 인식 중, 끄기는 정지 버튼' : '실시간 자막 켜기'}
              >
                <span className="cp-program-slide-webcam-btn-icon" aria-hidden>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" />
                    <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                    <line x1="12" x2="12" y1="19" y2="22" />
                  </svg>
                </span>
              </button>
              {captionOn ? (
                <>
                  <span className="cp-program-slide-bgm-nav-block-gap" aria-hidden />
                  <button
                    type="button"
                    className="cp-program-slide-caption-nav-block-btn cp-program-slide-caption-nav-block-btn--transport"
                    data-tutorial-pres="caption-stop"
                    onClick={() => stopCaption()}
                    title="자막 끄기"
                    aria-label="자막 끄기"
                  >
                    <span
                      className="cp-program-slide-webcam-btn-icon cp-program-slide-bgm-nav-block-icon--compact"
                      aria-hidden
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <rect x="7" y="7" width="10" height="10" rx="1.5" />
                      </svg>
                    </span>
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="cp-topnav-right">
          <div className="cp-topnav-account-page-zoom" role="group" aria-label="화면 배율">
            <button
              type="button"
              className="cp-topnav-account-page-zoom-btn"
              onClick={handlePageZoomOut}
              disabled={pageZoom <= PAGE_ZOOM_MIN}
              aria-label="페이지 축소"
              title={`화면 축소 (${formatPageZoomPercent(pageZoom)})`}
            >
              −
            </button>
            <button
              type="button"
              className="cp-topnav-account-page-zoom-btn"
              onClick={handlePageZoomIn}
              disabled={pageZoom >= PAGE_ZOOM_MAX}
              aria-label="페이지 확대"
              title={`화면 확대 (${formatPageZoomPercent(pageZoom)})`}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className="cp-theme-toggle cp-program-slide-fullscreen-btn"
            data-tutorial-pres="fullscreen"
            onClick={toggleFullscreen}
            title={fullscreen ? '전체 화면 해제' : '전체 화면'}
            aria-label={fullscreen ? '전체 화면 해제' : '전체 화면'}
          >
            {fullscreen ? <FullscreenShrinkIcon /> : <FullscreenExpandIcon />}
          </button>
          <button type="button" className="cp-theme-toggle" onClick={() => window.close()} title="탭 닫기">
            닫기
          </button>
        </div>
      </header>

      {/* 타이머는 EduCoordiTimerModal(ClassLab 플로팅 UI)에서 자체 표시 */}

      {/* 카메라(실물화상기 + 판서) 전체화면 오버레이 */}
      {webcamOn ? (
        <div
          className={
            'cp-dash-presentation-webcam-overlay' +
            (captionOn ? ' cp-dash-presentation-webcam-overlay--caption-on' : '')
          }
          role="region"
          aria-label="웹캠·화면 미리보기"
          data-tutorial-pres="webcam-overlay"
        >
          {webcamCaptureMode === 'screen_camera' ? (
            <div className="cp-dash-presentation-webcam-screen-fill" aria-hidden>
              <video
                ref={screenVideoRef}
                className="cp-dash-presentation-webcam-video cp-dash-presentation-webcam-video--contain"
                autoPlay
                playsInline
                muted
              />
            </div>
          ) : null}
          <div
            className={
              'cp-dash-presentation-webcam-crop' +
              (webcamCaptureMode === 'screen_camera' ? ' is-pip' : '') +
              (frozenFrameUrl ? ' is-frozen' : '')
            }
            data-tutorial-pres="webcam-screen"
            role={frozenFrameUrl ? 'img' : 'button'}
            tabIndex={frozenFrameUrl ? -1 : 0}
            title={
              frozenFrameUrl
                ? '일시정지됨. 상단 네비 웹캠 블록에서 재생합니다.'
                : '클릭: 미리보기 일시정지 (상단 네비 웹캠 블록에서도 가능)'
            }
            aria-label={
              frozenFrameUrl
                ? '일시정지된 미리보기. 상단 네비 웹캠 블록에서 재생'
                : '미리보기 일시정지'
            }
            aria-pressed={!!frozenFrameUrl}
            onClick={() => {
              if (!frozenFrameUrl) toggleWebcamFreeze();
            }}
            onKeyDown={(e) => {
              if (frozenFrameUrl) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleWebcamFreeze();
              }
            }}
          >
            <video
              ref={webcamVideoRef}
              className={
                'cp-dash-presentation-webcam-video' +
                (webcamCaptureMode === 'screen' ? ' cp-dash-presentation-webcam-video--contain' : '') +
                (webcamViewTransformed ? ' cp-dash-presentation-webcam-video--view-transformed' : '')
              }
              style={webcamViewStyle}
              autoPlay
              playsInline
              muted
            />
            {frozenFrameUrl ? (
              <img
                src={frozenFrameUrl}
                alt=""
                className={
                  'cp-dash-presentation-webcam-video cp-dash-presentation-webcam-freeze' +
                  (webcamViewTransformed ? ' cp-dash-presentation-webcam-video--view-transformed' : '')
                }
                style={webcamViewStyle}
              />
            ) : null}
          </div>
          {webcamLoading ? (
            <div className="cp-dash-presentation-webcam-loading" aria-live="polite">
              미디어 연결 중…
            </div>
          ) : null}
          <ProgramSlideWebcamDrawRoot
            strokes={webcamStrokes}
            onStrokesChange={setWebcamStrokes}
            webcamView={webcamViewControls}
          >
            <ProgramSlideWebcamDrawCanvas />
            <ProgramSlideWebcamDrawToolbar />
          </ProgramSlideWebcamDrawRoot>
        </div>
      ) : null}

      {/* 칠판 편집: 웹캠 OFF여도 판서 툴바·그리기 레이어 표시 */}
      {viewMode === 'board' && boardCanvasMode === 'edit' && !webcamOn ? (
        <div
          className="cp-dash-presentation-board-draw-overlay"
          role="region"
          aria-label="칠판 판서"
          data-tutorial-pres="board-draw"
        >
          <ProgramSlideWebcamDrawRoot strokes={boardDrawStrokes} onStrokesChange={onBoardDrawStrokesChange}>
            <ProgramSlideWebcamDrawCanvas />
            <ProgramSlideWebcamDrawToolbar />
          </ProgramSlideWebcamDrawRoot>
        </div>
      ) : null}

      {/* 실시간 자막: 켜진 동안 하단 그라데이션 영역을 깔고, 슬라이드와 동일 폰트·2줄 창 */}
      {captionOn ? (
        <div className="cp-dash-presentation-caption-layer" aria-live="polite" aria-atomic="true">
          <div className="cp-dash-presentation-caption-dim" aria-hidden />
          <div className="cp-dash-presentation-caption">
            <div
              className={
                'cp-program-slide-hero-live-caption-window' +
                (captionWinMode === 'single-center' ? ' is-caption-single-line' : '') +
                (captionWinMode === 'tail' ? ' is-caption-tail' : '')
              }
            >
              <div ref={captionInnerRef} className="cp-program-slide-hero-live-caption-inner">
                {captionText.trim() ? captionText : '\u00a0'}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {captionError ? (
        <div className="cp-dash-presentation-caption-error" role="alert">
          {captionError}
        </div>
      ) : null}
      {webcamError ? (
        <div className="cp-dash-presentation-caption-error" role="alert">
          {webcamError}
        </div>
      ) : null}

      {/* 모달들 — cp-program-slide-root[data-slide-surface]로 라이트/다크 스타일 적용 */}
      <EduCoordiTimerModal
        open={timerModalOpen}
        onClose={() => setTimerModalOpen(false)}
        onRunningChange={setTimerRunning}
      />
      <ProgramSlideBgmModal open={bgmModalOpen} onClose={() => setBgmModalOpen(false)} onPick={handleBgmPick} />
      <ProgramSlideDiceModal
        open={diceModalOpen}
        onClose={() => setDiceModalOpen(false)}
        memberLookup={workspaceId && gradeId ? { workspaceId, gradeId } : null}
      />
    </div>
  );
};

export default MainPresentationToolbar;
